'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform } from 'react-native';

import {
  DEFAULT_CONTACT_NUMBERS_LOS,
  DEFAULT_CONTACT_NUMBERS_LMS,
  type ContactNumbers,
} from '@app/configs/contactNumbers';
import {
  registerContactNumbersRefresh,
  type ContactNumbersRefreshOverride,
} from '@app/services/contactNumbersBridge';
import { fetchContactNumbers, type ContactNumbersJourney } from '@app/services/contactNumbers';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { updatePhoneNumber } from '@app/store/slices/commonSlice';

type ContactInfoState = ContactNumbers & {
  showWA: boolean;
  showCall: boolean;
  refresh: (override?: ContactNumbersRefreshOverride) => Promise<void>;
};

const ContactNumbersContext = createContext<ContactInfoState | undefined>(undefined);

type ContactNumbersProviderProps = {
  children: React.ReactNode;
};

const getFallback = (journey: ContactNumbersJourney): ContactNumbers => {
  return journey === 'LOS' ? DEFAULT_CONTACT_NUMBERS_LOS : DEFAULT_CONTACT_NUMBERS_LMS;
};

export function ContactNumbersProvider({ children }: ContactNumbersProviderProps) {
  const dispatch = useAppDispatch();
  const platform = useAppSelector((state) => state?.platformParams?.platform);
  const userPlatformCode = useAppSelector(
    (state) => state?.auth?.userDetails?.linkedPlatformAccounts?.[0]?.platformCode ?? null
  );
  const isLmsUser = useAppSelector((state) => state?.commonReducer?.isLMSUser);

  const { showWA, showCall } = useAppSelector((state) => {
    const csPillData =
      state?.auth?.userDetails?.linkedPlatformAccounts?.[0]?.platformSDKConfig?.csPillData;
    return {
      showWA: csPillData?.showWA !== false,
      showCall: csPillData?.showCall !== false,
    };
  });

  const journey: ContactNumbersJourney = isLmsUser ? 'LMS' : 'LOS';
  const platformCodeForContacts = userPlatformCode ?? platform ?? null;

  const [phoneNumber, setPhoneNumber] = useState<string>(getFallback(journey).phoneNumber);
  const [whatsappNumber, setWhatsappNumber] = useState<string>(getFallback(journey).whatsappNumber);

  const load = useCallback(async (override?: ContactNumbersRefreshOverride) => {
    const urlParams =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : null;

    const effectiveJourney = override?.journey ?? journey;
    const effectivePlatform = override?.platform ?? platformCodeForContacts;

    try {
      const data = await fetchContactNumbers({
        journey: effectiveJourney,
        platform: effectivePlatform,
        overriddenTelephoneNumber: urlParams?.get('callData') ?? null,
        overriddenWhatsappNumber: urlParams?.get('waData') ?? null,
      });
      setPhoneNumber(data.phoneNumber);
      setWhatsappNumber(data.whatsappNumber);
      if (effectiveJourney === 'LMS') {
        dispatch(updatePhoneNumber({ phoneNumber: data.phoneNumber }));
      }
    } catch {
      const fallback = getFallback(effectiveJourney);
      setPhoneNumber(fallback.phoneNumber);
      setWhatsappNumber(fallback.whatsappNumber);
    }
  }, [journey, platformCodeForContacts]);

  useEffect(() => {
    registerContactNumbersRefresh(load);
    return () => registerContactNumbersRefresh(null);
  }, [load]);

  const value = useMemo(
    () => ({
      phoneNumber,
      whatsappNumber,
      showWA,
      showCall,
      refresh: (override?: ContactNumbersRefreshOverride) => load(override),
    }),
    [phoneNumber, whatsappNumber, showWA, showCall, load]
  );

  return <ContactNumbersContext.Provider value={value}>{children}</ContactNumbersContext.Provider>;
}

export function useContactNumbers(): ContactInfoState {
  const ctx = useContext(ContactNumbersContext);
  if (!ctx) {
    return {
      phoneNumber: DEFAULT_CONTACT_NUMBERS_LOS.phoneNumber,
      whatsappNumber: DEFAULT_CONTACT_NUMBERS_LOS.whatsappNumber,
      showWA: true,
      showCall: true,
      refresh: async () => {},
    };
  }
  return ctx;
}

